import { useApiGateway } from '@/composables/useApi';
import { HttpStatus } from '../errors/enums/http-status.enum';
import { InternalError } from '../errors/internal.error';
import type { Subscription } from '@factoryfixinc/ats-interfaces';
import type { UpgradeProposal } from './types/upgrade-proposal.type';

export class SubscriptionPersistenceV2 {
  private readonly v1BaseUrl = 'subscription-gateway/v1';

  async fetchSubscription(employerId: number): Promise<Subscription | undefined> {
    const { error, data, statusCode } = await useApiGateway(
      `${this.v1BaseUrl}/subscription/employer/${employerId}`,
    )
      .get()
      .json<{
        subscription: Subscription;
      }>();

    // Only a genuine 404 means "no subscription on file". Anything else that
    // leaves us without data — network failure, 5xx, a proxy/LB error page —
    // must throw so callers can't mistake an outage for an expired plan.
    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return;
    }

    if (error.value || !data.value) {
      throw new InternalError('Could not load subscription information', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value.subscription;
  }

  async fetchUpgradeProposal(employerId: number): Promise<UpgradeProposal | null> {
    const { error, data, statusCode } = await useApiGateway(
      `${this.v1BaseUrl}/subscription/employer/${employerId}/upgrade-proposal`,
    )
      .get()
      .json<UpgradeProposal>();

    if (statusCode.value === HttpStatus.NOT_FOUND || !data.value) {
      return null;
    }

    if (error.value) {
      throw new InternalError('Could not fetch upgrade proposal', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value;
  }

  async fetchPortalSessionUrl(employerId: number): Promise<string> {
    const { error, data } = await useApiGateway(
      `${this.v1BaseUrl}/portal-session/employer/${employerId}`,
    )
      .post()
      .json<{
        portalSession: { access_url: string };
      }>();

    if (error.value || !data.value?.portalSession?.access_url) {
      throw new InternalError('Could not load billing portal session', {
        error: error.value,
      });
    }

    return data.value.portalSession.access_url;
  }
}
